import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
} from '@mui/material';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import { appColors } from '../theme/colors';
import { apiService } from '../services/api';
import { Project as ProjectType, CreateProject, UpdateProject, Client } from '../types/api';

export interface ProjectFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** When set, dialog is in edit mode with this project's data. When null, create mode. */
  project: ProjectType | null;
  /** Called after successful create or update so parent can refresh. */
  onSuccess: () => void;
}

const initialForm: CreateProject & { clientId?: number | null } = {
  name: '',
  siteAddress: '',
  clientId: undefined,
  budget: undefined,
  description: '',
  status: 'Draft',
};

const ProjectFormDialog: React.FC<ProjectFormDialogProps> = ({ open, onClose, project, onSuccess }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateProject & { clientId?: number | null }>(initialForm);

  const editingId = project?.projectId ?? null;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const clientList = await apiService.getClients();
        setClients(clientList);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (project) {
      setForm({
        name: project.name,
        siteAddress: project.siteAddress ?? '',
        clientId: project.clientId ?? undefined,
        budget: project.budget,
        description: project.description ?? '',
        status: project.status,
      });
    } else {
      setForm(initialForm);
    }
    setSaveError(null);
  }, [open, project]);

  const handleClose = () => {
    setSaveError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload: CreateProject | UpdateProject = {
        name: form.name.trim(),
        siteAddress: form.siteAddress?.trim() || null,
        clientId: form.clientId ?? null,
        budget: form.budget,
        description: form.description || undefined,
        status: form.status,
      };
      if (editingId != null) {
        await apiService.updateProject(editingId, payload as UpdateProject);
      } else {
        await apiService.createProject(payload as CreateProject);
      }
      onSuccess();
      handleClose();
    } catch (err) {
      console.error(err);
      setSaveError('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={listPageDialogPaperProps}
    >
      <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
        {editingId != null ? 'Edit Project' : 'Create New Project'}
      </DialogTitle>
      <DialogContent sx={{ padding: '20px 24px 0' }}>
        <div className="space-y-4 my-5 mx-0">
          {saveError && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-2 text-red-400 text-sm">
              {saveError}
            </div>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            required
            variant="outlined"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Waterfront Residence"
          />
          <TextField
            margin="dense"
            select
            label="Status"
            fullWidth
            variant="outlined"
            value={form.status ?? 'Draft'}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            <MenuItem value="Draft">Proposal</MenuItem>
            <MenuItem value="Active">In Progress</MenuItem>
            <MenuItem value="OnHold">On Hold</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Site Address"
            fullWidth
            variant="outlined"
            value={form.siteAddress ?? ''}
            onChange={(e) => setForm({ ...form, siteAddress: e.target.value })}
            placeholder="Type site address"
          />
          <TextField
            margin="dense"
            select
            label="Client"
            fullWidth
            variant="outlined"
            value={form.clientId ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                clientId: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          >
            <MenuItem value="">No client</MenuItem>
            {clients.map((c) => (
              <MenuItem key={c.clientId} value={c.clientId}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Estimated Budget"
            type="number"
            fullWidth
            variant="outlined"
            inputProps={{ min: 0, step: 1000 }}
            value={form.budget ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                budget: e.target.value ? Number(e.target.value) : undefined,
              })
            }
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Optional project notes"
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: appColors.mutedText,
            borderColor: appColors.borderDark,
            '&:hover': {
              borderColor: appColors.primary,
              color: appColors.primary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!form.name.trim() || saving}
          variant="contained"
          sx={{
            backgroundColor: appColors.primary,
            '&:hover': {
              backgroundColor: appColors.primaryDark,
            },
            '&.Mui-disabled': {
              backgroundColor: appColors.disabledBg,
              color: appColors.disabledText,
            },
          }}
        >
          {saving ? 'Saving…' : editingId != null ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectFormDialog;
